import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

const POLL_MS = 6000;

/** Same strategy as the Mobile app: poll /sync; when the server version changes, refresh what is on screen. Returns online state. */
export function useLiveSync(userId) {
  const qc = useQueryClient();
  const version = useRef(null);
  const [online, setOnline] = useState(true);
  useEffect(() => {
    if (!userId) return undefined;
    let timer = null;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const { version: v } = await api("/sync");
        if (version.current !== null && v !== version.current) qc.invalidateQueries();
        version.current = v;
        setOnline(true);
      } catch (err) {
        if (!err.status || err.status >= 500) setOnline(false);
      }
      if (!stopped && document.visibilityState === "visible") timer = setTimeout(tick, POLL_MS);
    };
    const start = () => { clearTimeout(timer); tick(); };
    const onVisible = () => { if (document.visibilityState === "visible") { qc.invalidateQueries(); start(); } else clearTimeout(timer); };
    const onOffline = () => setOnline(false);
    start();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", start);
    window.addEventListener("offline", onOffline);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", start);
      window.removeEventListener("offline", onOffline);
    };
  }, [userId, qc]);
  return online;
}
